import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Toolbar,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';

// Helper function to format duration days to readable format
const formatDuration = (days) => {
  if (!days) return '365 days (1 year)';
  const years = Math.floor(days / 365);
  const remaining = days % 365;
  const months = Math.floor(remaining / 30);

  if (years > 0 && months > 0) {
    return `${days} days (${years}y ${months}m)`;
  } else if (years > 0) {
    return `${days} days (${years} year${years > 1 ? 's' : ''})`;
  } else if (months > 0) {
    return `${days} days (${months} month${months > 1 ? 's' : ''})`;
  } else {
    return `${days} days`;
  }
};

// Default icons for vehicle and permit types. `labelKey` looks up the
// display label in typeManager.icons.<labelKey> — the emoji itself
// (`value`) is what's actually stored, so this is display-only.
const DEFAULT_ICONS = {
  vehicle: [
    { value: '🏍️', labelKey: 'motorcycle' },
    { value: '🛺', labelKey: 'rickshaw' },
    { value: '🚗', labelKey: 'car' },
    { value: '🚕', labelKey: 'taxi' },
    { value: '🚙', labelKey: 'suv' },
    { value: '🚚', labelKey: 'truck' },
    { value: '🚐', labelKey: 'van' },
    { value: '🚌', labelKey: 'bus' },
    { value: '🚎', labelKey: 'publicBus' },
    { value: '🏎️', labelKey: 'sportsCar' },
    { value: '🚛', labelKey: 'heavyTruck' },
    { value: '🛴', labelKey: 'eScooter' },
    { value: '🚲', labelKey: 'bicycle' },
  ],
  permit: [
    { value: '📄', labelKey: 'document' },
    { value: '🎫', labelKey: 'ticket' },
    { value: '🚨', labelKey: 'alert' },
    { value: '✅', labelKey: 'approved' },
    { value: '⏳', labelKey: 'pending' },
    { value: '❌', labelKey: 'rejected' },
    { value: '📋', labelKey: 'clipboard' },
    { value: '🏛️', labelKey: 'authority' },
    { value: '🔑', labelKey: 'license' },
    { value: '📍', labelKey: 'location' },
  ]
};

function TypeManager({ title, endpoint, defaultDurationDays }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [docTypeOptions, setDocTypeOptions] = useState([]);
  const [durationValue, setDurationValue] = useState('');

  // Fetch available document types for the required_document_types selector on permit types
  useEffect(() => {
    if (!endpoint.includes('permit')) return;
    apiClient.get('/document-types/').then(res => {
      const types = res.data.results || res.data || [];
      setDocTypeOptions(types.filter(t => t.is_active));
    }).catch(() => { });
  }, [endpoint]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(endpoint);
      setItems(response.data.results || response.data);
      setError('');
    } catch (err) {
      setError(t('typeManager.errorLoadFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [endpoint, t]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);



  const handleOpenDialog = (item = null) => {
    if (item) {
      setFormData(item);
      setDurationValue(item.permit_duration_days || '');
      setEditingId(item.id);
    } else {
      // For vehicle types, pre-fill with default value from org settings
      const initialData = {};
      if (endpoint.includes('vehicle')) {
        // Pre-fill with default so user can see and edit it
        const defaultVal = defaultDurationDays || 365;
        initialData.permit_duration_days = defaultVal;
        setDurationValue(defaultVal);
      } else {
        setDurationValue('');
      }
      setFormData(initialData);
      setEditingId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({});
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Keep durationValue in sync with formData for the duration field
    if (name === 'permit_duration_days') {
      setDurationValue(value);
    }
  };

  const handleClearDuration = () => {
    setDurationValue('');
    setFormData(prev => ({ ...prev, permit_duration_days: '' }));
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await apiClient.put(`${endpoint}${editingId}/`, formData);
        setSuccess(t('typeManager.successUpdated'));
      } else {
        await apiClient.post(endpoint, formData);
        setSuccess(t('typeManager.successCreated'));
      }
      fetchItems();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || t('typeManager.errorSaveFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('typeManager.confirmDelete'))) return;
    try {
      await apiClient.delete(`${endpoint}${id}/`);
      setSuccess(t('typeManager.successDeleted'));
      fetchItems();
    } catch (err) {
      setError(t('typeManager.errorDeleteFailed'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Toolbar sx={{ pl: 0, pr: 0, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          {title}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('typeManager.addNew')}
        </Button>
      </Toolbar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>{t('chalanDetail.name')}</TableCell>
              {endpoint.includes('permit') && !endpoint.includes('document') && <TableCell>{t('typeManager.codeCol')}</TableCell>}
              {endpoint.includes('document-type') && <TableCell>{t('typeManager.keyCol')}</TableCell>}
              {endpoint.includes('document-type') && <TableCell>{t('typeManager.requirementCol')}</TableCell>}
              {endpoint.includes('vehicle') && <TableCell>{t('typeManager.iconCol')}</TableCell>}
              {endpoint.includes('vehicle') && <TableCell>{t('typeManager.permitDurationCol')}</TableCell>}
              <TableCell>{t('permitSearch.description')}</TableCell>
              <TableCell>{t('permitList.status')}</TableCell>
              <TableCell>{t('permitList.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.name}</TableCell>
                {endpoint.includes('permit') && !endpoint.includes('document') && <TableCell><Chip label={item.code} size="small" /></TableCell>}
                {endpoint.includes('document-type') && <TableCell><Chip label={item.key} size="small" variant="outlined" /></TableCell>}
                {endpoint.includes('document-type') && (
                  <TableCell>
                    <Chip
                      label={item.is_required ? t('typeManager.compulsory') : t('typeManager.optional')}
                      size="small"
                      color={item.is_required ? 'error' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                )}
                {endpoint.includes('vehicle') && <TableCell sx={{ fontSize: '24px' }}>{item.icon}</TableCell>}
                {endpoint.includes('vehicle') && <TableCell><Chip label={formatDuration(item.permit_duration_days)} size="small" color="primary" variant="outlined" /></TableCell>}
                <TableCell>{item.description}</TableCell>
                <TableCell>
                  <Chip
                    label={item.is_active ? t('common.active') : t('common.inactive')}
                    color={item.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={t('common.edit')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(item)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.delete')}>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? t('typeManager.editTitle', { title }) : t('typeManager.addTitle', { title })}
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label={t('chalanDetail.name')}
            name="name"
            value={formData.name || ''}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          {endpoint.includes('permit') && !endpoint.includes('document') && (
            <>
              <TextField
                fullWidth
                label={t('typeManager.codeLabel')}
                name="code"
                value={formData.code || ''}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('typeManager.iconCol')}</InputLabel>
                <Select
                  label={t('typeManager.iconCol')}
                  name="icon"
                  value={formData.icon || ''}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">{t('typeManager.selectIcon')}</MenuItem>
                  {DEFAULT_ICONS.permit.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <span style={{ fontSize: '20px', marginRight: '8px' }}>{icon.value}</span>
                      {t(`typeManager.icons.${icon.labelKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.icon && (
                <Box sx={{ my: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>{t('typeManager.preview')}</Typography>
                  <Box sx={{ fontSize: '48px' }}>{formData.icon}</Box>
                </Box>
              )}
              {/* Document types selector with compulsory/optional toggle */}
              {(() => {
                // Normalise: old format was plain strings, new format is {key, required}
                const normalise = (val) => {
                  if (!Array.isArray(val)) return [];
                  return val.map(item => typeof item === 'string' ? { key: item, required: true } : item);
                };
                const selectedDocTypes = normalise(formData.required_document_types || []);
                const selectedKeys = selectedDocTypes.map(d => d.key);

                const handleKeysChange = (e) => {
                  const newKeys = e.target.value;
                  setFormData(prev => {
                    const existing = normalise(prev.required_document_types || []);
                    const updated = newKeys.map(k => {
                      const existing_entry = existing.find(d => d.key === k);
                      if (existing_entry) return existing_entry;
                      // Pre-fill required flag from the doc type's is_required default
                      const docTypeDef = docTypeOptions.find(o => o.key === k);
                      return { key: k, required: docTypeDef?.is_required !== false };
                    });
                    return { ...prev, required_document_types: updated };
                  });
                };

                const toggleRequired = (key, required) => {
                  setFormData(prev => ({
                    ...prev,
                    required_document_types: normalise(prev.required_document_types || []).map(d =>
                      d.key === key ? { ...d, required } : d
                    ),
                  }));
                };

                return (
                  <>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>{t('typeManager.documentTypesLabel')}</InputLabel>
                      <Select
                        multiple
                        label={t('typeManager.documentTypesLabel')}
                        value={selectedKeys}
                        onChange={handleKeysChange}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map(k => (
                              <Chip key={k} label={docTypeOptions.find(o => o.key === k)?.name || k} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {docTypeOptions.map((opt) => (
                          <MenuItem key={opt.key} value={opt.key}>{opt.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedDocTypes.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                          {t('typeManager.requirementLevelLabel')}
                        </Typography>
                        {selectedDocTypes.map(({ key, required }) => {
                          const label = docTypeOptions.find(o => o.key === key)?.name || key;
                          return (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, border: '1px solid #e0e0e0', borderRadius: 1, background: '#fafafa' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Chip
                                  label={t('typeManager.compulsory')}
                                  size="small"
                                  color={required ? 'error' : 'default'}
                                  variant={required ? 'filled' : 'outlined'}
                                  onClick={() => toggleRequired(key, true)}
                                  sx={{ cursor: 'pointer' }}
                                />
                                <Chip
                                  label={t('typeManager.optional')}
                                  size="small"
                                  color={!required ? 'success' : 'default'}
                                  variant={!required ? 'filled' : 'outlined'}
                                  onClick={() => toggleRequired(key, false)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                );
              })()}
            </>
          )}
          {endpoint.includes('document-type') && (
            <>
              <TextField
                fullWidth
                label={t('typeManager.keySlugLabel')}
                name="key"
                value={formData.key || ''}
                onChange={handleInputChange}
                margin="normal"
                required
                helperText={t('typeManager.keySlugHelper')}
              />
              <Box sx={{ mt: 1.5, px: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_required !== false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {formData.is_required !== false ? t('typeManager.compulsory') : t('typeManager.optional')}
                      </Typography>
                      <Chip
                        label={formData.is_required !== false ? t('typeManager.required') : t('typeManager.optional')}
                        size="small"
                        color={formData.is_required !== false ? 'error' : 'success'}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>
                  }
                />
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                  {t('typeManager.requirementNote')}
                </Typography>
              </Box>
            </>
          )}
          {endpoint.includes('vehicle') && (
            <>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>{t('typeManager.iconCol')}</InputLabel>
                <Select
                  label={t('typeManager.iconCol')}
                  name="icon"
                  value={formData.icon || ''}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">{t('typeManager.selectIcon')}</MenuItem>
                  {DEFAULT_ICONS.vehicle.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <span style={{ fontSize: '20px', marginRight: '8px' }}>{icon.value}</span>
                      {t(`typeManager.icons.${icon.labelKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.icon && (
                <Box sx={{ my: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>{t('typeManager.preview')}</Typography>
                  <Box sx={{ fontSize: '48px' }}>{formData.icon}</Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label={t('typeManager.permitDurationDaysLabel')}
                  name="permit_duration_days"
                  type="number"
                  value={durationValue}
                  onChange={handleInputChange}
                  margin="normal"
                  helperText={
                    durationValue
                      ? formatDuration(durationValue)
                      : t('typeManager.willUseOrgDefault', { days: defaultDurationDays || 365 })
                  }
                  inputProps={{ min: 1, max: 10950 }}
                  autoComplete="off"
                />
                {durationValue && (
                  <Button
                    size="small"
                    onClick={handleClearDuration}
                    sx={{ minWidth: 'auto', padding: '8px', marginTop: '8px' }}
                  >
                    {t('permitSearch.clear')}
                  </Button>
                )}
              </Box>
            </>
          )}
          <TextField
            fullWidth
            label={t('permitSearch.description')}
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? t('feeManagement.update') : t('feeManagement.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TypeManager;
